'use client';

import { get as _get, filter as _filter, reject as _reject, sortBy as _sortBy } from 'lodash-es';
import { Fragment, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import { makeStyles } from 'tss-react/mui';
import FoundGroupList from '@/components/found-group-list';
import GroupList from '@/components/group-list';
import MaterialLinkButton from '@/components/material-link-button';
import SectionHeader from '@/components/section-header';
import Main from '@/components/main';
import { myArchivedGroupsQuery } from '@/apollo/groups';
import { sortGroupsByRelevance } from '@/utils/sort-groups';
import { useAppContext } from '@/contexts/app';
import type { Group } from '@/types/Group';

interface CityData {
  id: string;
  slug: string;
  name: string;
  groups: Group[];
}

const useStyles = makeStyles()((theme) => ({
  createButton: {
    margin: theme.spacing(2, 0, 0, 0),
  },
}));

interface Props {
  city: CityData;
  myGroups: Group[] | null;
  bannerDismissed: boolean;
}

export default function GroupsIndexPageClient({ city, myGroups, bannerDismissed }: Props) {
  const { classes } = useStyles();
  const { loggedInUser } = useAppContext();
  const [showArchived, setShowArchived] = useState(false);
  const [fetchArchivedGroups, { data: archivedData, loading: archivedLoading }] =
    useLazyQuery(myArchivedGroupsQuery);
  const archivedGroups: Group[] | null = _get(archivedData, 'me.archivedGroups', null);

  const sortedMyGroups = useMemo(() => {
    if (!myGroups) return null;
    return sortGroupsByRelevance(myGroups, loggedInUser?.groupMembers);
  }, [myGroups, loggedInUser]);

  const cityGroups = useMemo(() => {
    if (!city) {
      return null;
    }
    const myGroupIds = myGroups ? new Set(myGroups.map((g) => g.id)) : new Set();
    const filtered = _reject(city.groups, (group) => {
      if (group.found) return true;
      return myGroupIds.has(group.id);
    });
    return _sortBy(filtered, [(group) => -(group.upcomingEventsCount || 0), 'name']);
  }, [city, myGroups]);

  const communityGroups = useMemo(() => {
    if (!city) {
      return null;
    }
    return _filter(city.groups, 'found');
  }, [city]);

  return (
    <Main compact signupBanner bannerDismissed={bannerDismissed}>
      <SectionHeader
        variant="h1"
        sx={{ mt: 3, mb: 3 }}
        action={
          <MaterialLinkButton
            href={`/groups/new?city=${city.slug}`}
            color="primary"
            variant="contained"
          >
            Create Group
          </MaterialLinkButton>
        }
      >
        Groups
      </SectionHeader>

      {sortedMyGroups && (
        <Fragment>
          <SectionHeader>My Groups</SectionHeader>
          <GroupList
            groups={sortedMyGroups}
            emptyMessage="You're not a member of any groups."
            showMembership={false}
          />
        </Fragment>
      )}

      <SectionHeader>{city.name} Groups</SectionHeader>
      <GroupList groups={cityGroups} />

      <MaterialLinkButton
        href={`/groups/new?city=${city.slug}`}
        color="primary"
        variant="contained"
        className={classes.createButton}
      >
        Create Group
      </MaterialLinkButton>

      {sortedMyGroups && (
        <Fragment>
          {!showArchived ? (
            <Button
              variant="text"
              size="small"
              sx={{ mt: 2, display: 'block' }}
              onClick={() => {
                setShowArchived(true);
                fetchArchivedGroups();
              }}
            >
              Show Archived Groups
            </Button>
          ) : (
            <Fragment>
              <Button
                variant="text"
                size="small"
                sx={{ mt: 2, display: 'block' }}
                onClick={() => setShowArchived(false)}
              >
                Hide Archived Groups
              </Button>
              <SectionHeader>Archived Groups</SectionHeader>
              {archivedLoading ? (
                <div>Loading...</div>
              ) : (
                <GroupList
                  groups={archivedGroups || []}
                  emptyMessage="No archived groups."
                  showMembership={false}
                />
              )}
            </Fragment>
          )}
        </Fragment>
      )}

      {communityGroups && communityGroups.length > 0 && (
        <Fragment>
          <SectionHeader>Also in {city.name}</SectionHeader>
          <FoundGroupList groups={communityGroups} />
        </Fragment>
      )}
    </Main>
  );
}
